/**
 * ComparisonNoteIndicator — indicateur discret de présence de notes (Chunk 9.Q.2).
 *
 * Rend un petit bouton SVG (note/bubble icon) + counter si > 1 note. Utilisable
 * sur 3 surfaces :
 *   - row scope    : positionné dans la 1ʳᵉ colonne "Champ" à côté du label
 *   - cell scope   : positionné top-right d'une `<td>` cellule
 *   - mobile card  : inline avec dt/dd dans la card stack
 *
 * Click → ouvre le `ComparisonNotesDialog` parent (callback `onOpen`).
 *
 * a11y :
 *   - `<button type="button">` (action, parité WIG)
 *   - `aria-label` bilingue avec count (live update)
 *   - `title` tooltip avec count (parité)
 *   - `focus-visible:ring-*` (WIG focus state)
 *   - `touch-manipulation` (WIG touch convention)
 *
 * Style :
 *   - bg-amber-50 + text-amber-700 ring-amber-200 (discret, signal mais pas
 *     envahissant — parité MissingInfoStatusBadge in-progress tone)
 *   - SVG inline 12x12 (anti-translate identifier comme code, parité)
 *   - counter rendu inline si count >= 2 (sinon icône seule)
 */

import { useTranslation } from "react-i18next";

type Variant = "row" | "cell" | "card";

type Props = {
  readonly count: number;
  readonly variant: Variant;
  readonly onOpen: () => void;
};

export function ComparisonNoteIndicator({ count, variant, onOpen }: Props) {
  const { t } = useTranslation();

  if (count <= 0) return null;

  const label = t("comparison_notes.indicator.label", { count });

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={label}
      title={label}
      className={
        "inline-flex shrink-0 items-center gap-0.5 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 ring-1 ring-inset ring-amber-200 " +
        "hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 touch-manipulation " +
        (variant === "cell" ? "ml-1 align-top" : "ml-1") +
        (variant === "card" ? "" : "")
      }
    >
      <svg
        width="10"
        height="10"
        viewBox="0 0 12 12"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M2 3a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H5l-2 2V3Z"
          stroke="currentColor"
          strokeWidth="1.25"
          strokeLinejoin="round"
        />
      </svg>
      {count >= 2 && <span className="tabular-nums">{count}</span>}
    </button>
  );
}
